/**
 * The language tags this site writes a sentence under.
 *
 * BCP-47 throughout, and the same tags that are already on the page: the
 * existing `hanzi` shortcode declares `zh-Hans-CN` and the `pinyin` one
 * declares `zh-Latn-CN-pinyin`, so the variants generated beside them are named
 * the same way rather than in a scheme of this repository's own.
 *
 * Script and reading standard are separate axes, which is why the hanzi tags
 * name a script (`Hans`, `Hant`) and the pinyin tags name a region. A mainland
 * edition of a classical text is 繁體 read with 普通话 readings, so conflating
 * the two would be wrong even though this site's pairs happen to line up.
 */

import type { ReaderSettings } from "../../user/reader-settings.js";
import type { ToolOptions } from "../../tools/options.js";

/** 简体, as the mainland writes it. */
export const simplifiedTag = "zh-Hans-CN";

/** 繁體, as Taiwan writes it. */
export const traditionalTag = "zh-Hant-TW";

/**
 * 繁體, as Hong Kong writes it.
 *
 * A third orthography rather than a dialect of the second. Hong Kong writes 58
 * characters differently from Taiwan: 説 for 說, 衞 for 衛, 温 for 溫, 裏 for 裡,
 * 牀 for 床. A reader who types one of those is typing the word rather than a
 * misspelling of it.
 *
 * There is no region-free 繁體 to write instead. `zh-Hant` means Taiwan's forms
 * in practice, which is why the conversion tool names both regions and neither
 * of them "Traditional" on its own. See `src/tools/script-panels.ts`.
 */
export const hongKongTag = "zh-Hant-HK";

/**
 * The writings in the order a sentence carries them.
 *
 * 简体 first, that being the one that was authored, then 繁體 as Taiwan writes
 * it, then as Hong Kong does. Each is nearer to the one before it than to the
 * 简体 line: Hong Kong writes 說 as 説 and 床 as 牀, and writes everything
 * Taiwan writes for the other 20,000 characters.
 *
 * That is what makes the order load-bearing rather than tidy.
 * `SentenceReader.distinct` drops a writing that repeats the one before it here
 * and `SentenceReadings.hanziIn` falls back along the same chain, so a line a
 * page does not carry is the line before it. `writingTags` below is in this
 * order too, and `partials/liju-writings.html` walks it out of
 * `reader_settings.json`.
 */
export const hanziTags: readonly string[] = [
  simplifiedTag,
  traditionalTag,
  hongKongTag,
];

/** Pinyin under 普通话 readings. */
export const mainlandPinyinTag = "zh-Latn-CN-pinyin";

/** Pinyin under 國語 readings. */
export const taiwanPinyinTag = "zh-Latn-TW-pinyin";

/** 注音符號, which is a script rather than a romanisation. */
export const bopomofoTag = "zh-Bopo";

/** The English translation, in the site's own variety of it. */
export const englishTag = "en-GB";

/**
 * The tags a writing is drawn and read under.
 *
 * The settings name a writing `zh-CN`, `zh-TW` or `zh-HK`, and the pages tag
 * their lines with the BCP-47 tags above. This is the one place the two
 * vocabularies meet, and `src/assets/reader-settings-data.ts` copies it into
 * `articles/data/reader_settings.json` so that a template can make the same
 * lookup Hugo cannot import.
 *
 * **The reading standard follows the writing**, which is a decision about
 * confusion rather than anything the data forces. Script and reading standard
 * are separate axes, as the tags above say, and offering both as choices would
 * ask a learner to settle a question most of them have never met.
 *
 * **Hong Kong reads as the mainland does.** Hong Kong has no Mandarin standard
 * of its own, and its schools teach 普通話 to the mainland's readings, so a
 * reader on Hong Kong's 繁體 gets the 普通话 reading under Hong Kong's
 * characters. Only Taiwan's 繁體 takes the 國語 reading. Until 2026-10-25 Hong
 * Kong took Taiwan's, which gave 艘 as sāo to a reader who would say sōu.
 *
 * `locale` is the same rule said in `@kensio/pinyinjs`'s vocabulary, which
 * knows two locales where this site names three writings. It is here rather
 * than in `src/tools` so that how Hong Kong reads is written down once: the
 * 例句 templates, the conversion tools and this table would otherwise be three
 * places to disagree about it.
 */
export const writingTags = {
  "zh-CN": {
    hanzi: simplifiedTag,
    pinyin: mainlandPinyinTag,
    locale: "zh-CN",
  },
  "zh-TW": {
    hanzi: traditionalTag,
    pinyin: taiwanPinyinTag,
    locale: "zh-TW",
  },
  "zh-HK": {
    hanzi: hongKongTag,
    pinyin: mainlandPinyinTag,
    locale: "zh-CN",
  },
} as const satisfies Readonly<
  Record<
    ReaderSettings["writing"],
    { hanzi: string; pinyin: string; locale: ToolOptions["locale"] }
  >
>;

/**
 * The writings read under one standard, as `data-cb-writing` names them.
 *
 * `zh-CN zh-HK` for 普通话 and `zh-TW` for 國語, read off {@link writingTags}
 * so that a line of pinyin is shown to the readers who say it that way.
 */
export function writingsReadAs(pinyinTag: string): string {
  return Object.entries(writingTags)
    .filter(([, tags]) => tags.pinyin === pinyinTag)
    .map(([writing]) => writing)
    .join(" ");
}
